import React from "react";
import { createPortal } from "react-dom";
import { FiFilter } from "react-icons/fi";

type Props = {
  label: string;
  children: React.ReactNode;
  /** Pixel width of the menu; tweak per your content */
  menuWidth?: number;
  /** Vertical gap between button and menu */
  offset?: number;
  /** z-index for the floating layer */
  zIndex?: number;
};

export default function ColumnFilter({
  label,
  children,
  menuWidth = 260,
  offset = 8,
  zIndex = 1000,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(
    null
  );
  const btnRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  const computePosition = React.useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();

    const vw = window.innerWidth;
    const desiredLeft = rect.left + rect.width - menuWidth; // right-align to button
    // Clamp left within viewport with a small margin
    const left = Math.max(8, Math.min(desiredLeft, vw - menuWidth - 8));
    const top = rect.bottom + offset;

    setPos({ top, left });
  }, [menuWidth, offset]);

  // Open/close handlers
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) computePosition();
  };

  // Close on outside click & on Escape
  React.useEffect(() => {
    if (!open) return;

    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t)) return;
      if (btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onReflow = () => computePosition();

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onReflow, true); // capture scroll anywhere
    window.addEventListener("resize", onReflow);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [open, computePosition]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="inline-flex items-center justify-center rounded p-0.5 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-500/30"
        onClick={toggle}
        aria-label={`Filter ${label}`}
        title={`Filter ${label}`}
      >
        <FiFilter />
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              style={{
                position: "fixed",
                top: pos.top,
                left: pos.left,
                width: menuWidth,
                zIndex,
              }}
              className="rounded-xl border border-gray-200 bg-white p-3 shadow-lg"
              role="dialog"
              aria-label={`${label} filter menu`}
            >
              <div className="mb-2 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                {label} Filter
              </div>
              <div className="space-y-2">{children}</div>
              <div className="mt-3 text-right">
                <button
                  className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
                  onClick={() => setOpen(false)}
                  type="button"
                >
                  Close
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
